import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { BookOpen, ArrowLeft, ArrowRight, FileText } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import type { GlossaryTerm } from "@/content/glossary/terms";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";

interface GlossaryDetailData {
  term: GlossaryTerm;
  categoryLabel: string;
  related: { slug: string; term: string }[];
  relatedServices: { slug: string; title: string }[];
  relatedInsights: { slug: string; title: string }[];
}

export const Route = createFileRoute("/glossary/$term")({
  component: GlossaryDetail,
  loader: async ({ params }): Promise<GlossaryDetailData> => {
    const [{ GLOSSARY, GLOSSARY_CATEGORIES, getGlossaryTerm }, { SERVICES }, { INSIGHTS_META: INSIGHTS }] =
      await Promise.all([
        import("@/content/glossary/terms"),
        import("@/lib/services"),
        import("@/content/insights/meta"),
      ]);
    const term = getGlossaryTerm(params.term);
    if (!term) throw notFound();
    return {
      term,
      categoryLabel: GLOSSARY_CATEGORIES[term.category],
      related: (term.relatedTerms ?? [])
        .map((s) => GLOSSARY.find((g) => g.slug === s))
        .filter((g): g is GlossaryTerm => Boolean(g))
        .map((g) => ({ slug: g.slug, term: g.term })),
      relatedServices: (term.relatedServiceSlugs ?? [])
        .map((s) => SERVICES.find((sv) => sv.slug === s))
        .filter((s): s is NonNullable<typeof s> => Boolean(s))
        .map((s) => ({ slug: s.slug, title: s.title })),
      relatedInsights: (term.relatedInsightSlugs ?? [])
        .map((s) => INSIGHTS.find((i) => i.slug === s))
        .filter((i): i is NonNullable<typeof i> => Boolean(i))
        .map((i) => ({ slug: i.slug, title: i.title })),
    };
  },
  head: ({ loaderData }) => {
    const term = loaderData?.term;
    if (!term) return {};
    const site = getActiveSiteUrl();
    const base = pageHeadForProvince({
      title: `${term.term} คืออะไร? นิยาม + ตัวอย่างใช้งาน — Thai Notary`,
      description: term.shortDef,
      path: `/glossary/${term.slug}`,
    });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "DefinedTerm",
            "@id": `${site}/glossary/${term.slug}#term`,
            name: term.term,
            alternateName: term.alsoKnownAs ?? [term.termEn],
            description: term.shortDef,
            url: `${site}/glossary/${term.slug}`,
            inDefinedTermSet: `${site}/glossary`,
            termCode: term.slug,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: "/" },
              { name: "อภิธานศัพท์", url: "/glossary" },
              { name: term.term, url: `/glossary/${term.slug}` },
            ]),
          ),
        },
        ...(term.faqs.length > 0
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify(buildFaqSchema(term.faqs)),
              },
            ]
          : []),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": `${site}/glossary/${term.slug}#webpage`,
            url: `${site}/glossary/${term.slug}`,
            name: term.term,
            description: term.shortDef,
            inLanguage: "th-TH",
            speakable: {
              "@type": "SpeakableSpecification",
              cssSelector: ["h1", "#ai-answer-tldr"],
            },
            mainEntity: { "@id": `${site}/glossary/${term.slug}#term` },
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/glossary/${term.slug}`)) },
      ],
    };
  },
});

function GlossaryDetail() {
  const { term, categoryLabel, related, relatedServices, relatedInsights } =
    Route.useLoaderData() as GlossaryDetailData;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-3xl px-4 py-12">
            <Link
              to="/glossary"
              className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-gold hover:underline"
            >
              <ArrowLeft className="h-3 w-3" /> Glossary
            </Link>
            <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-primary">
              <BookOpen className="h-3 w-3" /> {categoryLabel}
            </div>
            <h1 className="mt-3 text-3xl font-extrabold leading-tight md:text-4xl">
              {term.term}
            </h1>
            <div className="mt-1 text-sm text-muted-foreground">{term.termEn}</div>

            {/* AEO TL;DR / Quick Answer */}
            <div
              id="ai-answer-tldr"
              className="mt-6 rounded-2xl border-l-4 border-gold bg-card p-5"
            >
              <div className="text-[10px] font-bold uppercase tracking-widest text-gold">
                คำตอบสั้น · Quick Answer
              </div>
              <p className="mt-1 text-base font-medium leading-relaxed text-foreground">
                {term.shortDef}
              </p>
            </div>

            {term.alsoKnownAs && term.alsoKnownAs.length > 0 && (
              <div className="mt-4 text-xs text-muted-foreground">
                <span className="font-bold">เรียกอีกอย่างว่า: </span>
                {term.alsoKnownAs.join(" · ")}
              </div>
            )}
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto max-w-3xl px-4 py-10">
            <h2 className="text-xl font-bold">นิยามและรายละเอียด</h2>
            <div className="prose prose-sm mt-4 max-w-none space-y-4 text-[15px] leading-relaxed text-foreground">
              {term.longDef.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            {term.stats && term.stats.length > 0 && (
              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                {term.stats.map((s, i) => (
                  <div key={i} className="rounded-xl border border-border bg-card p-4">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gold">
                      {s.label}
                    </div>
                    <div className="mt-1 text-base font-extrabold text-foreground">
                      {s.value}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {term.faqs.length > 0 && (
          <section className="border-b border-border bg-muted/30">
            <div className="mx-auto max-w-3xl px-4 py-10">
              <h2 className="text-xl font-bold">คำถามที่พบบ่อย · FAQ</h2>
              <div className="mt-5 space-y-4">
                {term.faqs.map((f, i) => (
                  <div key={i} className="rounded-xl border border-border bg-card p-5">
                    <div className="font-bold text-foreground">{f.q}</div>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {(related.length > 0 || relatedServices.length > 0 || relatedInsights.length > 0) && (
          <section className="border-b border-border">
            <div className="mx-auto max-w-3xl px-4 py-10">
              {related.length > 0 && (
                <>
                  <h2 className="text-xl font-bold">คำที่เกี่ยวข้อง</h2>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {related.map((r) => (
                      <Link
                        key={r.slug}
                        to="/glossary/$term"
                        params={{ term: r.slug }}
                        className="rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold text-foreground hover:border-gold"
                      >
                        {r.term}
                      </Link>
                    ))}
                  </div>
                </>
              )}

              {relatedServices.length > 0 && (
                <>
                  <h3 className="mt-8 text-base font-bold">บริการที่เกี่ยวข้อง</h3>
                  <ul className="mt-3 space-y-2 text-sm">
                    {relatedServices.map((s) => (
                      <li key={s!.slug}>
                        <a
                          href={`/services/${s!.slug}`}
                          className="inline-flex items-center gap-2 text-primary hover:underline"
                        >
                          <FileText className="h-3.5 w-3.5" /> {s!.title}
                          <ArrowRight className="h-3 w-3" />
                        </a>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {relatedInsights.length > 0 && (
                <>
                  <h3 className="mt-8 text-base font-bold">บทความที่เกี่ยวข้อง</h3>
                  <ul className="mt-3 space-y-2 text-sm">
                    {relatedInsights.map((p) => (
                      <li key={p!.slug}>
                        <a
                          href={`/insights/${p!.slug}`}
                          className="inline-flex items-center gap-2 text-primary hover:underline"
                        >
                          <BookOpen className="h-3.5 w-3.5" /> {p!.title}
                          <ArrowRight className="h-3 w-3" />
                        </a>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </section>
        )}
        <div className="mx-auto max-w-4xl px-4 pb-12">
          <FaqSourceNote topic="consular" />
        </div>

      </main>
      <SiteFooter />
    </div>
  );
}